import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    CircularProgress,
    alpha,
    useTheme
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Login as LoginIcon,
    Lock as LockIcon,
    Person as PersonIcon,
    ArrowForward as ArrowIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { companyName } = useSettings();
    const theme = useTheme();

    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const success = await login(formData.username, formData.password);
        setLoading(false);

        if (success) {
            navigate('/dashboard');
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* CSS Animation for Spin */}
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    @keyframes float {
                        0% { transform: translateY(0px); }
                        50% { transform: translateY(-20px); }
                        100% { transform: translateY(0px); }
                    }
                `}
            </style>

            {/* Background Orbs */}
            <Box sx={{
                position: 'absolute',
                top: '-10%',
                right: '-10%',
                width: '50vw',
                height: '50vw',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(46,125,50,0.2) 0%, rgba(0,0,0,0) 70%)',
                filter: 'blur(60px)',
                animation: 'float 15s ease-in-out infinite'
            }} />
            <Box sx={{
                position: 'absolute',
                bottom: '-10%',
                left: '-10%',
                width: '50vw',
                height: '50vw',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,111,0,0.15) 0%, rgba(0,0,0,0) 70%)',
                filter: 'blur(60px)',
                animation: 'float 20s ease-in-out infinite reverse'
            }} />

            {/* Login Card */}
            <Paper
                elevation={24}
                sx={{
                    p: { xs: 4, sm: 6 },
                    width: '100%',
                    maxWidth: 450,
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    zIndex: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: '0 20px 80px rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,255,255,0.2)'
                }}
            >
                {/* Brand Header */}
                <Box sx={{
                    mb: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                }}>
                    {/* Animated Helix Logo */}
                    <Box sx={{
                        position: 'relative',
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1
                    }}>
                        {/* Outer Ring */}
                        <Box sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            border: '3px solid transparent',
                            borderTopColor: theme.palette.primary.main,
                            borderBottomColor: theme.palette.secondary.main,
                            borderRadius: '50%',
                            animation: 'spin 3s linear infinite',
                            filter: 'drop-shadow(0 0 8px rgba(46,125,50,0.3))'
                        }} />
                        {/* Inner Ring */}
                        <Box sx={{
                            position: 'absolute',
                            width: '70%',
                            height: '70%',
                            border: '3px solid transparent',
                            borderLeftColor: theme.palette.primary.light,
                            borderRightColor: theme.palette.secondary.light,
                            borderRadius: '50%',
                            animation: 'spin 2s linear infinite reverse',
                            opacity: 0.7
                        }} />
                        {/* Core Logo */}
                        <Box
                            component="img"
                            src="/buds-logo.png"
                            alt="Logo"
                            sx={{
                                width: 45,
                                height: 45,
                                objectFit: 'contain',
                                zIndex: 1
                            }}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/45?text=B'; }}
                        />
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                        <Box
                            component="img"
                            src="/buds-full-logo.png"
                            alt="Budscube"
                            sx={{
                                width: 'auto',
                                height: 24,
                                mb: 1,
                                opacity: 0.8
                            }}
                        />
                        <Typography variant="h5" fontWeight={800} sx={{
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            letterSpacing: '-0.5px'
                        }}>
                            {companyName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Sign in to your enterprise dashboard
                        </Typography>
                    </Box>
                </Box>

                {/* Form */}
                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <TextField
                        fullWidth
                        label="Username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        margin="normal"
                        required
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonIcon color="action" />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: 3,
                                bgcolor: 'rgba(240,242,245,0.5)',
                                '&:hover': {
                                    bgcolor: 'rgba(240,242,245,0.8)'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0,0,0,0.05)'
                                }
                            }
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        margin="normal"
                        required
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: 3,
                                bgcolor: 'rgba(240,242,245,0.5)',
                                '&:hover': {
                                    bgcolor: 'rgba(240,242,245,0.8)'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0,0,0,0.05)'
                                }
                            }
                        }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                            mt: 4,
                            mb: 3,
                            py: 1.8,
                            borderRadius: 3,
                            fontSize: '1rem',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                            boxShadow: '0 8px 20px rgba(46, 125, 50, 0.25)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 25px rgba(46, 125, 50, 0.35)',
                            }
                        }}
                        endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowIcon />}
                    >
                        {loading ? 'Authenticating...' : 'Sign In'}
                    </Button>
                </Box>

                {/* Footer */}
                <Box sx={{ mt: 'auto', pt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', opacity: 0.7 }}>
                        Powered by Budscube Technology
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                        &copy; {new Date().getFullYear()} All rights reserved
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
};

export default Login;
